import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  accountQuery,
  latestCasinoWinsQuery,
  useAuthSession,
} from '@/api';
import {
  assignLatestWinMultipliers,
  formatLatestWinAmount,
  isLatestRoundsAndBetsEnabled,
  type EnrichedLatestCasinoWin,
} from '@/domain';
import { cn } from '@/lib/cn';
import { Icon, Spinner } from '@/ui/common';

export interface LatestWinsProps {
  className?: string;
  /** Gold “See all >” pill linking to `/casino`. Default true (home). */
  showSeeAll?: boolean;
  /**
   * Compact list height for game details (~4–5 rows under the fade).
   * Default false shows ~10 rows (home / casino lobby).
   */
  compact?: boolean;
  /** Override the clipped list max-height in px (e.g. desktop game details). */
  listMaxHeight?: number;
  /** Rows kept in the ticker window. Default 10 (lobby/home). */
  visibleCount?: number;
}

/** Rows kept in the ticker window (legacy + Figma lobby/home). */
const VISIBLE_COUNT_DEFAULT = 10;
/** 10×36px rows + 9×5px gaps — full lobby/home list. */
const LIST_MAX_H_DEFAULT = 405;
/** Game-details clipped list (previous LatestWinsList height). */
const LIST_MAX_H_COMPACT = 193;
/** Desktop game-details: 10 rows. */
export const VISIBLE_COUNT_DETAILS_DESKTOP = 10;
/** 10×36px rows + 9×5px gaps. */
export const LIST_MAX_H_DETAILS_DESKTOP = 405;
/** Legacy `LatestTransactionsAnimationDuration` — class lifecycle between exit/enter. */
const ANIM_MS = 60;
/** Legacy: `Math.floor(Math.random() * 221) + 80`. */
const DELAY_MIN_MS = 80;
const DELAY_SPAN_MS = 221;

type VisibleRow = EnrichedLatestCasinoWin & {
  rowId: number;
  phase: 'enter' | 'exit' | 'idle';
};

function LatestWinRow({ row }: { row: VisibleRow }) {
  const highlight = row.hot;
  return (
    <div
      className={cn(
        'flex h-9 items-center gap-2 rounded-sm bg-bg-elevated px-3 py-2',
        highlight && 'border-[1.5px] border-solid border-status-warning',
        row.phase === 'enter' && 'animate-latest-win-enter',
        row.phase === 'exit' && 'animate-latest-win-exit',
      )}
    >
      <p className="min-w-0 flex-1 truncate text-xs text-text-primary">{row.Game}</p>
      <div
        className={cn(
          'flex w-20 shrink-0 items-center justify-end',
          highlight && 'gap-1 text-status-warning',
        )}
      >
        {highlight ? <Icon name="flash" size={12} className="text-status-warning" /> : null}
        <p
          className={cn(
            'text-xs font-bold',
            highlight ? 'text-status-warning' : 'text-text-secondary',
          )}
        >
          {row.multiplierLabel}
        </p>
      </div>
      <p className="w-20 shrink-0 text-right text-[11px] font-bold text-status-warning">
        {formatLatestWinAmount(row.Amount, row.Currency)}
      </p>
    </div>
  );
}

/**
 * Figma LatestWins — vertical Game / Multiplier / Amount table with legacy
 * fake multipliers and client-side ticker. Gated by `LatestRoundsAndBets`.
 */
export function LatestWins({
  className,
  showSeeAll = true,
  compact = false,
  listMaxHeight,
  visibleCount = VISIBLE_COUNT_DEFAULT,
}: LatestWinsProps) {
  const { user } = useAuthSession();
  const { data: account } = useQuery(accountQuery());
  const enabled = isLatestRoundsAndBetsEnabled(account, user);
  const accountGuid = account?.Guid ?? '';
  const listMaxH =
    listMaxHeight ?? (compact ? LIST_MAX_H_COMPACT : LIST_MAX_H_DEFAULT);
  const windowSize = Math.max(1, visibleCount);

  const winsQuery = useQuery({
    ...latestCasinoWinsQuery(accountGuid),
    enabled: enabled && Boolean(accountGuid),
  });

  const enriched = useMemo(
    () => assignLatestWinMultipliers(winsQuery.data ?? []),
    [winsQuery.data],
  );

  const [visible, setVisible] = useState<VisibleRow[]>([]);
  const nextIndexRef = useRef(0);
  const rowIdRef = useRef(0);
  const enrichedRef = useRef(enriched);
  const pausedRef = useRef(false);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleNextRef = useRef<() => void>(() => {});
  enrichedRef.current = enriched;

  // Seed visible rows whenever the enriched list identity changes.
  useEffect(() => {
    if (enriched.length === 0) {
      setVisible([]);
      nextIndexRef.current = 0;
      return;
    }
    const count = Math.min(windowSize, enriched.length);
    const seed: VisibleRow[] = [];
    for (let i = 0; i < count; i++) {
      const win = enriched[i];
      if (!win) continue;
      rowIdRef.current += 1;
      seed.push({ ...win, rowId: rowIdRef.current, phase: 'idle' });
    }
    setVisible(seed);
    nextIndexRef.current = count;
  }, [enriched, windowSize]);

  /**
   * Mirrors legacy `animateNextTransaction`:
   * - random delay 80–300ms
   * - exit last → after 60ms remove + prepend with enter → after 60ms clear enter
   * - schedule the next delay immediately (does not wait for anim to finish)
   * - pause clears only the delay timer; resume calls schedule again
   */
  useEffect(() => {
    if (enriched.length <= windowSize) return;

    const clearDelay = () => {
      if (delayTimerRef.current != null) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
    };

    const scheduleNext = () => {
      if (pausedRef.current) return;
      clearDelay();
      const randomDelay = Math.floor(Math.random() * DELAY_SPAN_MS) + DELAY_MIN_MS;
      delayTimerRef.current = setTimeout(() => {
        if (pausedRef.current) return;
        const source = enrichedRef.current;
        if (source.length <= windowSize) return;

        setVisible((prev) => {
          if (prev.length === 0) return prev;
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (!last) return prev;
          copy[copy.length - 1] = { ...last, phase: 'exit' };
          return copy;
        });

        const idx = nextIndexRef.current % source.length;
        const nextWin = source[idx];
        if (!nextWin) return;
        nextIndexRef.current = (idx + 1) % source.length;

        if (exitTimerRef.current != null) clearTimeout(exitTimerRef.current);
        exitTimerRef.current = setTimeout(() => {
          rowIdRef.current += 1;
          const enteringId = rowIdRef.current;
          setVisible((prev) => [
            { ...nextWin, rowId: enteringId, phase: 'enter' },
            ...prev.slice(0, -1),
          ]);
          if (enterTimerRef.current != null) clearTimeout(enterTimerRef.current);
          enterTimerRef.current = setTimeout(() => {
            setVisible((prev) =>
              prev.map((row) =>
                row.rowId === enteringId ? { ...row, phase: 'idle' } : row,
              ),
            );
          }, ANIM_MS);
        }, ANIM_MS);

        // Legacy: call animateNextTransaction() immediately — next delay overlaps anim.
        scheduleNext();
      }, randomDelay);
    };

    scheduleNextRef.current = scheduleNext;
    scheduleNext();

    return () => {
      clearDelay();
      if (exitTimerRef.current != null) clearTimeout(exitTimerRef.current);
      if (enterTimerRef.current != null) clearTimeout(enterTimerRef.current);
      exitTimerRef.current = null;
      enterTimerRef.current = null;
    };
  }, [enriched, windowSize]);

  const pause = () => {
    pausedRef.current = true;
    if (delayTimerRef.current != null) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
  };

  const resume = () => {
    pausedRef.current = false;
    if (enrichedRef.current.length > windowSize) {
      scheduleNextRef.current();
    }
  };

  if (!enabled) return null;

  const isLoading = winsQuery.isLoading;
  if (!isLoading && enriched.length === 0) return null;

  return (
    <section
      className={cn('flex w-full flex-col gap-2 [overflow-anchor:none]', className)}
      aria-label="Latest Wins"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon name="flash" size={24} className="shrink-0 text-text-primary" />
          <h2 className="text-base font-bold leading-6 text-text-primary">LATEST WINS</h2>
        </div>
        {showSeeAll ? (
          <Link
            to="/casino"
            className="shrink-0 rounded-full bg-status-warning px-3 py-1.5 text-xs font-bold text-bg-elevated"
          >
            See all &gt;
          </Link>
        ) : null}
      </div>

      {isLoading && visible.length === 0 ? (
        <div
          className="flex items-center justify-center"
          style={{ height: listMaxH }}
        >
          <Spinner label="Loading wins" />
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 px-3 py-2 text-[10px] text-text-secondary">
            <span className="min-w-0 flex-1">Game</span>
            <span className="w-20 shrink-0 text-right">Multiplier</span>
            <span className="w-20 shrink-0 text-right">Amount</span>
          </div>
          {/*
            Fixed height + overflow-anchor:none — prepending ticker rows would
            otherwise trigger browser scroll anchoring and jump the page.
          */}
          <div
            className="relative flex flex-col gap-[5px] overflow-hidden [overflow-anchor:none]"
            style={{ height: listMaxH }}
            onPointerDown={pause}
            onPointerUp={resume}
            onPointerCancel={resume}
          >
            {visible.map((row) => (
              <LatestWinRow key={row.rowId} row={row} />
            ))}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[72px] bg-gradient-to-b from-transparent to-bg-canvas" />
          </div>
        </>
      )}
    </section>
  );
}
